import { morph, MORPH_OPTIONS } from '@theme/morph';

class ProductListTabs extends HTMLElement {
  #tabTriggers = [];
  #templates = new Map();
  #activeContainer = null;
  #abortController = null;
  #activeTabId = null;
  #prefetchedTabs = new Set();
  #prefetchedImageUrls = new Set();

  connectedCallback() {
    this.#cacheElements();
    if (!this.#activeContainer || this.#tabTriggers.length === 0) return;

    this.#bindEvents();

    const selectedTab =
      this.#tabTriggers.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? this.#tabTriggers[0];
    if (selectedTab) {
      const selectedTabId = selectedTab.dataset.tabId;
      this.#setActiveTab(selectedTabId, false);
      this.#warmupTabImages(selectedTabId);
      this.#scheduleIdlePrefetch(selectedTabId);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #cacheElements() {
    this.#tabTriggers = Array.from(this.querySelectorAll('[data-tab-trigger]'));
    this.#activeContainer = this.querySelector('[data-tabs-active-content]');
    this.#templates = new Map(
      Array.from(this.querySelectorAll('template[data-tab-content]')).map((template) => [template.dataset.tabContent, template])
    );
  }

  #bindEvents() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    for (const [index, tab] of this.#tabTriggers.entries()) {
      tab.addEventListener(
        'click',
        (event) => {
          const tabId = tab.dataset.tabId;
          if (!tabId || !this.#templates.has(tabId)) return;
          event.preventDefault();
          this.#setActiveTab(tabId);
        },
        { signal }
      );

      tab.addEventListener(
        'pointerenter',
        () => {
          const tabId = tab.dataset.tabId;
          if (!tabId) return;
          this.#warmupTabImages(tabId);
        },
        { signal }
      );

      tab.addEventListener(
        'focus',
        () => {
          const tabId = tab.dataset.tabId;
          if (!tabId) return;
          this.#warmupTabImages(tabId);
        },
        { signal }
      );

      tab.addEventListener(
        'keydown',
        (event) => {
          this.#handleKeyboardNavigation(event, index);
        },
        { signal }
      );
    }
  }

  #handleKeyboardNavigation(event, currentIndex) {
    const { key } = event;
    let nextIndex = null;

    if (key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % this.#tabTriggers.length;
    } else if (key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + this.#tabTriggers.length) % this.#tabTriggers.length;
    } else if (key === 'Home') {
      nextIndex = 0;
    } else if (key === 'End') {
      nextIndex = this.#tabTriggers.length - 1;
    } else if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      const tabId = this.#tabTriggers[currentIndex]?.dataset.tabId;
      if (tabId) this.#setActiveTab(tabId);
      return;
    } else {
      return;
    }

    event.preventDefault();
    const nextTab = this.#tabTriggers[nextIndex];
    if (!nextTab) return;

    nextTab.focus();
    const tabId = nextTab.dataset.tabId;
    if (tabId) {
      this.#setActiveTab(tabId);
    }
  }

  #setActiveTab(tabId, shouldSwapContent = true) {
    if (!tabId) return;
    if (!this.#templates.has(tabId)) return;
    if (!this.#activeContainer) return;
    if (this.#activeTabId === tabId) return;

    this.#activeTabId = tabId;

    for (const tab of this.#tabTriggers) {
      const isActive = tab.dataset.tabId === tabId;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    }

    if (!shouldSwapContent) return;

    const template = this.#templates.get(tabId);
    if (!template) return;

    this.#swapActiveContent(template);
  }

  #swapActiveContent(template) {
    if (!this.#activeContainer) return;

    const nextContent = template.content.cloneNode(true);

    if (!this.#activeContainer.hasChildNodes()) {
      this.#activeContainer.replaceChildren(nextContent);
      this.#refreshOverflowLists();
      return;
    }

    morph(this.#activeContainer, nextContent, MORPH_OPTIONS);
    this.#refreshOverflowLists();
  }

  #refreshOverflowLists() {
    requestAnimationFrame(() => {
      if (!this.#activeContainer) return;

      const overflowLists = this.#activeContainer.querySelectorAll('swatches-variant-picker-component overflow-list');

      for (const overflowList of overflowLists) {
        for (const overflowedItem of overflowList.querySelectorAll(':scope > [slot="overflow"]')) {
          overflowedItem.slot = '';
        }

        overflowList.dispatchEvent(
          new CustomEvent('reflow', {
            bubbles: true,
            detail: {},
          })
        );
      }
    });
  }

  #scheduleIdlePrefetch(activeTabId) {
    const prefetched = new Set([activeTabId]);
    const queue = this.#tabTriggers
      .map((tab) => tab.dataset.tabId)
      .filter((tabId) => tabId && !prefetched.has(tabId));

    const runner = () => {
      for (const tabId of queue) {
        this.#warmupTabImages(tabId);
      }
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(runner, { timeout: 1200 });
    } else {
      window.setTimeout(runner, 250);
    }
  }

  #warmupTabImages(tabId) {
    if (!tabId || this.#prefetchedTabs.has(tabId)) return;
    const template = this.#templates.get(tabId);
    if (!template) return;

    this.#prefetchedTabs.add(tabId);
    const images = template.content.querySelectorAll('img');

    for (const image of images) {
      const src = image.getAttribute('src');
      const srcset = image.getAttribute('srcset');
      const sizes = image.getAttribute('sizes');

      if (src && !this.#prefetchedImageUrls.has(src)) {
        this.#prefetchedImageUrls.add(src);
        const preloader = new Image();
        if (srcset) preloader.srcset = srcset;
        if (sizes) preloader.sizes = sizes;
        preloader.src = src;
      }
    }
  }

}

if (!customElements.get('product-list-tabs')) {
  customElements.define('product-list-tabs', ProductListTabs);
}
